import { FC, ReactNode, useEffect, useState } from 'react';
import { InlineNotification, Modal, NumberInput } from '@carbon/react';
import type { StartProcessResponse } from './types';
import { startProcessRequest } from './shared';
import styles from './page.module.scss';

// Shared shell for the two launch modals (Normalize / Generate): error
// notification, submitting state, the optional-limit NumberInput, and the
// POST → StartProcessResponse unwrap. State resets every time the modal opens
// (it stays mounted while closed, so component state would otherwise leak
// between opens — stale error, stale limit).
export const LaunchProcessModal: FC<{
  open: boolean;
  heading: string;
  primaryButtonText: string;
  url: string;
  // Omit to launch without a batch-size cap.
  limitLabel?: string;
  size?: 'sm' | 'md' | 'lg';
  submitDisabled?: boolean;
  buildBody: (limit: number | '') => Record<string, unknown>;
  onClose: () => void;
  onStarted: (proc: StartProcessResponse) => void;
  children: ReactNode;
}> = ({
  open,
  heading,
  primaryButtonText,
  url,
  limitLabel,
  size,
  submitDisabled = false,
  buildBody,
  onClose,
  onStarted,
  children,
}) => {
  const [limit, setLimit] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLimit('');
    setError(null);
    setSubmitting(false);
  }, [open]);

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    const res = await startProcessRequest(url, buildBody(limit));
    setSubmitting(false);
    if (res.data) {
      onStarted(res.data);
      onClose();
    } else {
      setError(res.error ?? 'Request failed');
    }
  };

  return (
    <Modal
      open={open}
      size={size}
      modalHeading={heading}
      primaryButtonText={submitting ? 'Starting…' : primaryButtonText}
      secondaryButtonText="Cancel"
      primaryButtonDisabled={submitting || submitDisabled}
      onRequestSubmit={submit}
      onRequestClose={onClose}
    >
      <div className={styles.modalForm}>
        {error && (
          <InlineNotification
            kind="error"
            title="Error"
            subtitle={error}
            lowContrast
            onClose={() => setError(null)}
          />
        )}
        {children}
        {limitLabel && (
          <NumberInput
            id={`${heading.toLowerCase().replace(/\s+/g, '-')}-limit`}
            label={limitLabel}
            min={0}
            allowEmpty
            value={limit}
            onChange={(_e, { value }) =>
              setLimit(value === '' ? '' : Number(value))
            }
          />
        )}
      </div>
    </Modal>
  );
};
